import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';
import { FadeView } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

export const QuantityStepperFloatingContainer = (props: Props) => {
  const { size = 'md', isShown, children } = props;

  const containerStyles = [styles.container, sizeStyles[size]];
  const outerContainerStyles = [styles.outerContainer, outerSizeStyles[size]];

  return (
    <FadeView show={isShown} duration={theme.transitions.short} style={outerContainerStyles}>
      <View style={containerStyles}>{children}</View>
    </FadeView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTAINER_PADDING_SM = 0;
const CONTAINER_PADDING = theme.spacing['1'];
const CONTAINER_MIN_WIDTH_SM = 82;
const CONTAINER_MIN_WIDTH_MD = 154;
const CONTAINER_MIN_WIDTH_LG = 175;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    position: 'absolute',
    zIndex: theme.zIndex.dropdown,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.WHITE,
    padding: CONTAINER_PADDING,
  },
});

const sizeStyles = StyleSheet.create<Record<Size, ViewStyle>>({
  sm: {
    minWidth: CONTAINER_MIN_WIDTH_SM,
    borderRadius: 27,
    padding: CONTAINER_PADDING_SM,
    gap: theme.spacing['1'],
  },
  md: {
    minWidth: CONTAINER_MIN_WIDTH_MD,
    borderRadius: 30,
  },
  lg: {
    minWidth: CONTAINER_MIN_WIDTH_LG,
    borderRadius: 40,
  },
});

const outerSizeStyles = StyleSheet.create<Record<Size, ViewStyle>>({
  sm: {
    top: -CONTAINER_PADDING_SM,
    right: -CONTAINER_PADDING_SM,
  },
  md: {
    top: -CONTAINER_PADDING,
    right: -CONTAINER_PADDING,
  },
  lg: {
    top: -CONTAINER_PADDING,
    right: -CONTAINER_PADDING,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  isShown: boolean;
  size?: Size;
}>;

type Size = 'sm' | 'md' | 'lg';
